import { createStyleContext } from '@/lib/create-style-context'
import { Link } from '@/navigation'
import { styled } from '@/styled-system/jsx'
import { segmentGroup } from '@/styled-system/recipes'
import { SegmentGroup as ArkSegmentGroup } from '@ark-ui/react/segment-group'
import {
  forwardRef,
  type ComponentProps,
  type ElementRef,
  type ReactNode,
} from 'react'

const { withProvider, withContext } = createStyleContext(segmentGroup)

const StyledRoot = withProvider(styled(ArkSegmentGroup.Root), 'root')
const StyledIndicator = withContext(
  styled(ArkSegmentGroup.Indicator),
  'indicator'
)

export interface SegmentGroupProps extends ComponentProps<typeof StyledRoot> {}

export const SegmentGroup = forwardRef<
  ElementRef<typeof StyledRoot>,
  SegmentGroupProps
>((props, ref) => {
  const { children, ...rest } = props

  return (
    <StyledRoot ref={ref} {...rest}>
      {children}
      <StyledIndicator />
    </StyledRoot>
  )
})

SegmentGroup.displayName = 'SegmentGroup'

const StyledItem = withContext(styled(ArkSegmentGroup.Item), 'item')
const StyledItemControl = withContext(
  styled(ArkSegmentGroup.ItemControl),
  'itemControl'
)
const StyledItemText = withContext(styled(ArkSegmentGroup.ItemText), 'itemText')

export interface SegmentGroupItemProps
  extends ComponentProps<typeof StyledItem> {
  children?: ReactNode
  href: string
}

export const SegmentGroupItem = forwardRef<
  ElementRef<typeof StyledItem>,
  SegmentGroupItemProps
>((props, ref) => {
  const { children, href, ...rest } = props

  return (
    <StyledItem ref={ref} {...rest} asChild>
      <Link href={href}>
        <StyledItemControl />
        <StyledItemText>{children}</StyledItemText>
      </Link>
    </StyledItem>
  )
})

SegmentGroupItem.displayName = 'SegmentGroupItem'
